import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router/dom';
import { createRouter } from './app/routes';
import { IliMark } from './components/Brand';
import { ToastProvider } from './components/Toast';
import { ApiError } from './lib/api';
import { SettingsProvider } from './lib/settings';
import { startOutboxSync } from './offline/outbox';
import './styles/theme.css';
import './styles/base.css';
import './styles/shell.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // The service worker answers from cache when offline, so always try the network first.
      networkMode: 'offlineFirst',
      retry: (n, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && n < 2,
    },
    mutations: { networkMode: 'always' },
  },
});

function Splash({ state }: { state: 'loading' | 'error' }) {
  return (
    <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', padding: 24, textAlign: 'center' }}>
      <div className="stack" style={{ alignItems: 'center' }}>
        <IliMark size={56} />
        {state === 'loading' ? (
          <div className="spinner" role="status" aria-label="Loading" />
        ) : (
          <>
            <h1 style={{ fontSize: 'var(--fs-xl)' }}>We can't connect right now</h1>
            <p className="muted">Check your internet connection and try again.</p>
            <div className="row row-wrap" style={{ justifyContent: 'center' }}>
              <button className="btn" onClick={() => location.reload()}>
                Try again
              </button>
              <a className="btn btn-emergency" href="tel:911">
                Emergency? Call 911
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function App() {
  const router = useMemo(() => createRouter(), []);
  return <RouterProvider router={router} />;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <SettingsProvider fallback={(state) => <Splash state={state} />}>
          <App />
        </SettingsProvider>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);

startOutboxSync();

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((err) => console.warn('Service worker not registered', err));
  });
}

